import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { forgotPassword, confirmNewPassword } from '../auth/cognito';
import './Login.css';

const STEP_REQUEST  = 'request';
const STEP_CONFIRM  = 'confirm';
const STEP_DONE     = 'done';

export default function ForgotPassword() {
  const navigate = useNavigate();

  const [step, setStep]           = useState(STEP_REQUEST);
  const [email, setEmail]         = useState('');
  const [code, setCode]           = useState('');
  const [newPassword, setNewPwd]  = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [error, setError]         = useState(null);
  const [busy, setBusy]           = useState(false);

  async function onRequest(e) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await forgotPassword(email.trim());
      setStep(STEP_CONFIRM);
    } catch (err) {
      setError(err.message || 'Could not send reset code.');
    } finally {
      setBusy(false);
    }
  }

  async function onConfirm(e) {
    e.preventDefault();
    setError(null);
    if (newPassword !== confirmPwd) {
      setError('Passwords do not match.');
      return;
    }
    setBusy(true);
    try {
      await confirmNewPassword(email.trim(), code.trim(), newPassword);
      setStep(STEP_DONE);
    } catch (err) {
      setError(err.message || 'Could not reset password.');
    } finally {
      setBusy(false);
    }
  }

  if (step === STEP_DONE) {
    return (
      <section className="login-page">
        <div className="login-card">
          <h1 className="login-title">Password updated</h1>
          <p className="login-sub">Your password has been reset. You can now sign in with your new credentials.</p>
          <button className="btn btn-primary" style={{ width: '100%' }} onClick={() => navigate('/login')}>
            Go to sign in
          </button>
        </div>
      </section>
    );
  }

  if (step === STEP_CONFIRM) {
    return (
      <section className="login-page">
        <div className="login-card">
          <h1 className="login-title">Reset your password</h1>
          <p className="login-sub">
            Enter the code we sent to <strong>{email}</strong> and choose a new password.
          </p>

          <form onSubmit={onConfirm} className="login-form">
            <label className="login-field">
              <span>Reset code</span>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                required
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="123456"
              />
            </label>

            <label className="login-field">
              <span>New password</span>
              <input
                type="password"
                autoComplete="new-password"
                required
                minLength={10}
                value={newPassword}
                onChange={(e) => setNewPwd(e.target.value)}
              />
              <span className="login-hint">Min. 10 characters, upper &amp; lowercase and a number.</span>
            </label>

            <label className="login-field">
              <span>Confirm new password</span>
              <input
                type="password"
                autoComplete="new-password"
                required
                value={confirmPwd}
                onChange={(e) => setConfirmPwd(e.target.value)}
              />
            </label>

            {error && <p className="login-error">{error}</p>}

            <button type="submit" className="btn btn-primary" disabled={busy}>
              {busy ? 'Resetting…' : 'Reset password'}
            </button>
          </form>

          <p className="login-foot">
            <button className="link-btn" onClick={() => setStep(STEP_REQUEST)}>
              ← Use a different email
            </button>
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="login-page">
      <div className="login-card">
        <h1 className="login-title">Forgot password?</h1>
        <p className="login-sub">Enter your email and we'll send you a reset code.</p>

        <form onSubmit={onRequest} className="login-form">
          <label className="login-field">
            <span>Email</span>
            <input
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>

          {error && <p className="login-error">{error}</p>}

          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? 'Sending…' : 'Send reset code'}
          </button>
        </form>

        <p className="login-foot">
          <Link to="/login">← Back to sign in</Link>
        </p>
      </div>
    </section>
  );
}
